<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select
        class="input-label col-span-12 md:col-span-2"
        v-model="selectedValue"
      >
        <el-option key="quickView" :label="$t('views.common.listView.quickView')" value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
        </el-option>
        <el-option
          key="export"
          :label="$t('views.common.listView.selectedRows.export')"
          value="export"
        >
        </el-option>
      </el-select>

      <el-input
        :placeholder="$t('views.common.listView.searchRows')"
        class="w-full col-span-12 md:col-span-6"
        :prefix-icon="Search"
        v-model="searchTerm"
      />

      <el-select
        class="input-label col-span-12 md:col-span-2"
        v-model="selectedLocation"
        clearable
        filterable
        :placeholder="locationFilterPlaceholder"
        value-key="id"
        @change="onLocationFilterChange"
      >
        <el-option
          v-for="option in locations"
          :key="option.id"
          :label="$ctx.locale === 'en' ? option.enName : option.arName"
          :value="option"
        ></el-option>
      </el-select>

      <div class="w-full col-span-12 md:col-span-2">
        <template v-if="$can('create', '/accounting/transactions')">
          <horizontal-button
            :title="$t('views.accounting.transactions.newTransaction')"
            @click.prevent="$router.push($Route.ACCOUNTING_TRANSACTIONS_NEW_TRANSACTION)"
          />
        </template>
      </div>
    </div>

    <ag-grid-vue
      v-if="rowModelType"
      class="ag-theme-alpine mt-4 md:mt-0"
      :columnDefs="columnDefs"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      :defaultColDef="defaultColDef"
      @cell-double-clicked="onCellDoubleClicked"
      :rowData="rowData"
      :pagination="selectedValue == 'allItems'"
      :paginationAutoPageSize="selectedValue == 'allItems'"
      :onGridReady="onGridReady"
      :rowBuffer="rowBuffer"
      :rowModelType="rowModelType"
      :cacheBlockSize="cacheBlockSize"
      :cacheOverflowSize="cacheOverflowSize"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :infiniteInitialRowCount="infiniteInitialRowCount"
      :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
  ProcessCellForExportParams
} from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'

import {
  Transaction,
  TransactionsPaginated,
  transactionI18nMessages
} from '@/models/accounting/Transaction'
import Location from '@/models/company/Location'
import { useI18n } from 'vue-i18n'
import { parseFormat } from '@/utils/date'
import { formatDinero, formatMoney } from '@/utils/money'
import { Search } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { onBeforeMount } from 'vue'
ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'transactions',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...transactionI18nMessages.en,
          selectLocation: 'Select Location'
        },
        ar: {
          ...transactionI18nMessages.ar,
          selectLocation: 'اختر الموقع'
        }
      },
      unescape: 'global'
    })

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      t,
      Search,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Transaction[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}
    const locations: Location[] = []

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,
      locations,

      searchTerm: '',
      selectedValue: 'quickView',
      // null = omit location_id so server defaults to session location
      selectedLocation: null as Location | null,
      gridApi: undefined as GridApi | undefined
    }
  },

  components: {
    AgGridVue,
    HorizontalButton
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: this.$t('models.common.id'),
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true
      },
      {
        headerName: this.$t('models.common.createdAt'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueFormatter: (params) => {
          return parseFormat(params.value)
        }
      },
      {
        headerName: this.t('amount'),
        field: 'amountAmount',
        valueFormatter: (params) => {
          if (!params.value) return formatMoney(0, this.$ctx.currency);
          const transaction = params.node?.data as Transaction
          return formatDinero(transaction.amountDinero)
        }
      },
      {
        headerName: this.t('fromAccount'),
        width: 400,
        valueGetter: (params) => {
          if (!params.data) return ''
          const transaction = params.node?.data as Transaction
          return this.$ctx.locale === 'en'
            ? transaction.creditedAccount.enName
            : transaction.creditedAccount.arName
        }
      },
      {
        headerName: this.t('toAccount'),
        width: 400,
        valueGetter: (params) => {
          if (!params.data) return ''
          const transaction = params.node?.data as Transaction
          return this.$ctx.locale === 'en'
            ? transaction.debitedAccount.enName
            : transaction.debitedAccount.arName
        }
      },
      // {
      //   headerName: this.t('journalType'),
      //   field: 'journalType',
      //   valueFormatter: (params) => {
      //     if (!params.value) return ''
      //     return this.t(params.value)
      //   }
      // },
      {
        headerName: this.t('user'),
        field: 'user.fullName'
      }
    ]
  },

  computed: {
    locationFilterPlaceholder() {
      const current = this.$ctx.currentLocation
      if (current) {
        return this.$ctx.locale === 'en' ? current.enName : current.arName
      }
      return this.t('selectLocation')
    }
  },

  async mounted() {
    await this.loadLocations()
    this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected)
          } else {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  },

  methods: {
    async loadLocations() {
      try {
        const data = await this.$http.get<Location[]>(Location.ENDPOINT)
        this.locations = data.map((location) => Location.from(location))
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }
    },

    appendLocationFilter(url: string) {
      // Only override when the user explicitly picks a location.
      // Cleared/empty keeps the server session-location default.
      if (this.selectedLocation?.id) {
        const separator = url.includes('?') ? '&' : '?'
        return `${url}${separator}location_id=${this.selectedLocation.id}`
      }
      return url
    },

    onLocationFilterChange() {
      if (this.rowModelType === 'infinite') {
        this.gridApi?.purgeInfiniteCache()
        return
      }
      this.updateRowData()
    },

    async updateRowData() {
      this.gridApi?.showLoadingOverlay()
      try {
        const url = this.appendLocationFilter(Transaction.ENDPOINT)
        const data = await this.$http.get<Transaction[]>(url)
        this.rowData = data.map((transaction) => Transaction.from(transaction))
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          let url = `${Transaction.ENDPOINT}?startRow=${params.startRow}&endRow=${params.endRow}`
          if (params.sortModel.length) {
            url += `&colId=${params.sortModel[0].colId}&sort=${params.sortModel[0].sort}`
          }

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          url = this.appendLocationFilter(url)

          const res = await this.$http.get<TransactionsPaginated>(url)
          this.rowData = res.data.map((transaction) => Transaction.from(transaction))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      }
    },

    processCellForExport(params: ProcessCellForExportParams) {
      if (params.column.getColDef().headerName === 'Transaction Amount') {
        return params.node?.data.amountDinero ? formatDinero(params.node?.data.amountDinero) : ''
      } else {
        //if the cell doesn't belong to any of the above don't change it
        return params.value
      }
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    onCellDoubleClicked(event: any) {
      this.$router.push(
        this.$Route.ACCOUNTING_TRANSACTIONS_TRANSACTION.replace(':id', event.data.id)
      )
    }
  }
})
</script>

<style></style>
